import { IAngularEvent, IOnInit } from "angular";
import { IQuery } from "../../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import { PartTransaction, PartTransactionItem } from "./model";
import {
  ObjectDetailService,
  ObjectIdentifier,
  vsServiceName as ObjectDetailServiceName,
} from "./objectDetailService/service";

import {
  vsServiceName as SelectRecordModalServiceName,
  SelectRecordModalService,
} from "../../../../records/services/selectRecordModal";

import {
  vsServiceName as RecordGridViewServiceName,
  RecordGridViewService,
  GridConfiguration,
} from "../../../../records/views/recordGridView";
import { RecordDataSource } from "../../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../../records/grids/columns/columnDefinitionService";
import { NullGridSettings } from "../../../../records/grids/settings/gridSettingsService";
import angularGridValueGetters from "../../../../utils/AngularGrid/valueGetters";
import { moneyValueGetterFactory } from "../../../../records/grids/cells/valueGetters";
import { default as SelectPartModal } from "../../../controls/selectPartModal";
import { Record as SlotTrakRecord } from "../../../../records/server/crud";
import { sortBy } from "lodash";
import {
  ActionFilter,
  EmptyConfig,
  PartTransactionComponentConfig,
} from "./config";

interface PartTransactionViewItem {
  transaction: PartTransaction;
  expanded: boolean;
  detailsLoaded: boolean;
  detailNegativeItems: PartTransactionItemViewItem[];
  detailPositiveItems: PartTransactionItemViewItem[];
  loading: boolean;
  objectIdentifier?: ObjectIdentifier;
}

interface PartTransactionItemViewItem extends PartTransactionItem {
  objectIdentifier: ObjectIdentifier | undefined;
  objectDetail: unknown | undefined;
  ContextObj: unknown;
}

function defaultQueryResultInfo() {
  return {
    currentTransactionItems: <PartTransactionViewItem[]>[],
    currentTotal: 0,
    currentPage: 1,
    currentPageSize: 50,
    firstItem: 1,
    lastItem: 50,
  };
}

export class PartTransactionController implements IOnInit {
  private queryResultInfo = defaultQueryResultInfo();
  private additionalContext: any | undefined = undefined;

  actionQueryOptions: ActionFilter[] = [{ display: "All", value: undefined }];

  private readonly machineGridConfiguration: GridConfiguration;
  private readonly userGridConfiguration: GridConfiguration;

  private readonly calendarToggleState: Record<string, boolean> = {
    Before: false,
    After: false,
  };

  /* bindings */
  private defaultQuery: IQuery = {};
  private config: PartTransactionComponentConfig = EmptyConfig;
  /* end bindings */

  private query: IQuery = {};

  static $inject = [
    ObjectDetailServiceName,
    ServerUtilityName,
    SelectRecordModalServiceName,
    RecordGridViewServiceName,
    ColumnDefinitionServiceName,
    SelectPartModal.vsName,
  ];
  constructor(
    private readonly objectDetailService: ObjectDetailService,
    private readonly serverUtility: ServerUtility,
    private readonly selectRecordModalService: SelectRecordModalService,
    recordGridViewService: RecordGridViewService,
    private readonly columnDefinitionService: ColumnDefinitionService,
    private readonly selectPartModal: SelectPartModal,
  ) {
    this.machineGridConfiguration =
      recordGridViewService.getGridConfiguration("Machine")!;

    this.userGridConfiguration = {
      gridSettings: new NullGridSettings(),
      ...recordGridViewService.getGridConfiguration("user")!,
    };
  }

  $onInit() {
    this.config ??= EmptyConfig;
    this.initializeFilters();
    this.fetchTransactions();
  }

  private initializeFilters() {
    let actionFilters = sortBy(this.config.actionFilters, (f) => f.display);
    this.actionQueryOptions.push(...actionFilters);
  }

  calendarOpenToggle(key: string, $e: IAngularEvent) {
    if (!Object.keys(this.calendarToggleState).includes(key)) {
      return;
    }
    const currentState = this.calendarToggleState[key];
    this.calendarToggleState[key] = !currentState;
    $e.preventDefault();
    $e.stopPropagation?.();
  }

  filtersChanged() {
    this.queryResultInfo = defaultQueryResultInfo();
    this.fetchTransactions();
  }

  private getEffectiveQuery() {
    return {
      ...this.query,
      ...this.defaultQuery,
    };
  }

  fetchTransactions() {
    const { currentPage, currentPageSize } = this.queryResultInfo;

    const effectiveQuery = {
      ...this.getEffectiveQuery(),
      OrderBy: "-Id",
      Skip: (currentPage - 1) * currentPageSize,
      Take: currentPageSize,
    };

    this.serverUtility
      .query<PartTransaction>({
        url: "/api/parts/partTransaction",
        query: effectiveQuery,
      })
      .then((queryResponse) => {
        this.queryResultInfo.currentTotal = queryResponse.Total;
        this.queryResultInfo.firstItem = effectiveQuery.Skip + 1;
        this.queryResultInfo.lastItem =
          queryResponse.Results.length + this.queryResultInfo.firstItem - 1;
        this.queryResultInfo.currentTransactionItems =
          this.buildTransactionViewItems(queryResponse.Results);
      });
  }

  download() {
    this.serverUtility
      .sendApiRequest<string>({
        method: "get",
        url: "/api/parts/partTransaction/flat.csv",
        params: this.getEffectiveQuery(),
      })
      .then((transactions: string) => {
        const content = new Blob([transactions], { type: "text/csv" });
        return this.serverUtility.saveFile({
          content,
          filename: "partTransactions.csv",
        });
      });
  }

  openPartModal() {
    const that = this;
    that.selectPartModal.open({
      multiple: false,
      partSelected(part: SlotTrakRecord) {
        const additionalContext = (that.additionalContext ??= {});
        additionalContext.part = part;
        that.query.PartId = part?.Id;
        that.filtersChanged();
      },
    });
  }

  openMachineModal() {
    const that = this;
    that.selectRecordModalService.openModal({
      gridConfiguration: that.machineGridConfiguration,
      title: "Select Machine",
      onItemSelected(item, modalInstance) {
        that.setContextValue("MachineId", item.MachineId, { machine: item });
        modalInstance.close(item);
        that.filtersChanged();
      },
    });
  }

  openPartOrderModal() {
    const that = this;
    that.selectRecordModalService.openModal({
      gridConfiguration: {
        columns: [
          that.columnDefinitionService.getStringColDef({
            field: "InvoiceNumber",
            header: "Invoice Number",
            suppressSorting: true,
          }),
          that.columnDefinitionService.getOptionColDef({
            field: "Status",
            options: [
              {
                display: "Open",
                value: "Open",
              },
              {
                display: "Partial",
                value: "Partial",
              },
              {
                display: "Complete",
                value: "Complete",
              },
            ],
          }),
          that.columnDefinitionService.getStringColDef({
            field: "VendorName",
            header: "Vendor",
            suppressSorting: true,
            valueGetter: angularGridValueGetters.byPath("Vendor.Name"),
          }),
          that.columnDefinitionService.getStringColDef({
            field: "PartOrderShippingType",
            header: "Shipping Type",
            suppressSorting: true,
            suppressMenu: true,
            valueGetter: angularGridValueGetters.byPath(
              "PartOrderShippingType.Name",
            ),
          }),
          that.columnDefinitionService.getStringColDef({
            field: "PoNumber",
            header: "PO Number",
            suppressSorting: true,
          }),
          that.columnDefinitionService.getStringColDef({
            field: "RqNumber",
            header: "Requisition Number",
            suppressSorting: true,
          }),
          that.columnDefinitionService.getDateColDef({
            field: "OrderDate",
            header: "Order Date",
            suppressSorting: true,
          }),
          that.columnDefinitionService.getNumberColDef({
            field: "TotalCost",
            header: "Total Cost",
            valueGetter: moneyValueGetterFactory(),
            suppressSorting: true,
            suppressMenu: true,
          }),
          that.columnDefinitionService.getNumberColDef({
            field: "QuantityOrdered",
            header: "QuantityOrdered",
            suppressMenu: true,
            suppressSorting: true,
          }),
        ],
        gridSettings: new NullGridSettings(),
        gridDataSource: new RecordDataSource(
          that.serverUtility,
          "/api/parts/orders",
        ),
      },
      onItemSelected(item, modalInstance) {
        that.setContextValue("PartOrderId", item.Id, { partOrder: item });
        modalInstance.close(item);
        that.filtersChanged();
      },
      title: "Select Part Order",
    });
  }

  openUserModal() {
    const that = this;
    that.selectRecordModalService.openModal({
      gridConfiguration: that.userGridConfiguration,
      title: "Select User",
      onItemSelected(item, modalInstance) {
        const additionalContext = (that.additionalContext ??= {});
        additionalContext.user = item;
        that.query.UserAuthId = item.Id;
        modalInstance.close(item);
        that.filtersChanged();
      },
    });
  }

  openPartPropertyModal() {
    const that = this;
    that.selectRecordModalService.openModal({
      gridConfiguration: {
        columns: [
          that.columnDefinitionService.getStringColDef({
            field: "Name",
            header: "Room",
          }),
          that.columnDefinitionService.getStringColDef({
            field: "PropertyName",
            header: "Property",
            suppressSorting: true,
            valueGetter: angularGridValueGetters.byPath("Property.Name"),
          }),
        ],
        gridSettings: new NullGridSettings(),
        gridDataSource: new RecordDataSource(
          that.serverUtility,
          "/api/parts/properties",
        ),
      },
      onItemSelected(item, modalInstance) {
        that.setContextValue("PartPropertyId", item.Id, { partProperty: item });
        modalInstance.close(item);
        that.filtersChanged();
      },
      title: "Select Part Room",
    });
  }

  clearFilters() {
    this.query = {};
    this.additionalContext = {};
    this.filtersChanged();
  }

  private setContextValue(
    contextPath: string | undefined,
    contextValue: any | undefined,
    additionalContext?: any | undefined,
  ) {
    this.query.ContextPath = contextPath;
    this.query.ContextValue = contextValue;
    this.additionalContext = additionalContext;
  }

  private buildTransactionViewItems(
    partTransactions: PartTransaction[],
  ): PartTransactionViewItem[] {
    function parseContext(item: PartTransactionItem) {
      try {
        return JSON.parse(item.Context);
      } catch {
        return undefined;
      }
    }

    return partTransactions.map((pt) => ({
      detailNegativeItems: pt.PartTransactionItems.filter(
        (i) => i.QuantityChange < 0,
      ).map((pti) => ({
        ...pti,
        objectIdentifier: this.buildObjectIdentifier(pti),
        objectDetail: undefined,
        ContextObj: parseContext(pti),
      })),
      detailPositiveItems: pt.PartTransactionItems.filter(
        (i) => i.QuantityChange > 0,
      ).map((pti) => ({
        ...pti,
        objectIdentifier: this.buildObjectIdentifier(pti),
        objectDetail: undefined,
        ContextObj: parseContext(pti),
      })),
      detailsLoaded: false,
      expanded: false,
      transaction: pt,
      loading: false,
    }));
  }

  toggleItems(tran: PartTransactionViewItem) {
    const expanded = (tran.expanded = !tran.expanded);

    if (!expanded) {
      return;
    }
    if (tran.detailsLoaded) {
      return;
    }

    const itemsWithIdentifiers = [
      ...tran.detailNegativeItems,
      ...tran.detailPositiveItems,
    ].filter((i) => i.objectIdentifier !== undefined);

    if (itemsWithIdentifiers.length === 0) {
      console.warn("No identifiers found", tran);
      tran.detailsLoaded = true;
      return;
    }

    tran.loading = true;

    this.objectDetailService
      .fetchObjects(itemsWithIdentifiers.map((i) => i.objectIdentifier!))
      .then((objectItems) => {
        for (const objectItem of objectItems) {
          for (const partTransactionItem of itemsWithIdentifiers.filter(
            (i) =>
              i.objectIdentifier!.type === objectItem.identifier.type &&
              i.objectIdentifier!.id === objectItem.identifier.id,
          )) {
            partTransactionItem.objectDetail = objectItem.object;
          }
        }
      })
      .finally(() => {
        tran.detailsLoaded = true;
        tran.loading = false;
      });
  }

  buildObjectIdentifier(
    item: PartTransactionItem,
  ): ObjectIdentifier | undefined {
    const ctx = JSON.parse(item.Context ?? "");

    switch (item.TableName) {
      case "PartOrderItem":
        return ctx.PartOrderId
          ? { type: "PartOrderItem", id: ctx.PartOrderId }
          : undefined;

      case "PartLocation":
        return ctx.PartPropertyId
          ? { type: "PartLocation", id: ctx.PartPropertyId }
          : undefined;

      case "MachinePart":
        return ctx.MachineId
          ? { type: "MachinePart", id: ctx.MachineId }
          : undefined;

      case "CheckedOutPart":
        return ctx.CheckedOutById
          ? { type: "CheckedOutPart", id: ctx.CheckedOutById }
          : undefined;

      case "VoidPartLocation":
        return ctx.VoidPartLocationId
          ? { type: "VoidPartLocation", id: ctx.VoidPartLocationId }
          : undefined;

      default:
        return undefined;
    }
  }
}
